import { prop } from 'ramda';
import { useMemo } from 'react';
import { useUserSelector } from 'js/store/hooks';
import { useShowLogin } from 'js/hooks/useShowLogin';
import { TUserAuthLoginMode } from 'js/hooks/user/store/types';
import { useLoginPopup } from 'js/components/popups/LoginPopup';
import { getFitToPlaySubmissionRequired, resendLogin2FAToken } from 'js/hooks/auth/pas';
import { useGetTwoFactorConf, useBatchRequest, useValidateLoginSession, useInvokeAction } from 'server';
import {
    useLoginByGetParams,
    useLoginByCredentialsWithRecaptchaPopup,
    useGetTemporaryAuthenticationToken,
    useAddCredentialsLoginHook,
    useAddBeforeRegistrationHook,
    useGetBeforeRegistrationHandler,
    useRegister,
    useLogout
} from 'js/hooks/auth/hooks';

export const useAuthPublicAPI = () => {
    const logout = useLogout();
    const register = useRegister();
    const showLogin = useShowLogin();
    const showLoginPopup = useLoginPopup();
    const batchRequest = useBatchRequest();
    const invokeAction = useInvokeAction();
    const loginByTemporaryToken = useLoginByGetParams();
    const validateLoginSession = useValidateLoginSession();
    const addAfterCredentialsLoginCallback = useAddCredentialsLoginHook();
    const addBeforeRegistrationHook = useAddBeforeRegistrationHook();
    const getBeforeRegistrationHandler = useGetBeforeRegistrationHandler();
    const getTemporaryAuthenticationToken = useGetTemporaryAuthenticationToken();
    const loginByCredentialsWithRecaptchaPopup = useLoginByCredentialsWithRecaptchaPopup();
    const getTwoFactorConfiguration = useGetTwoFactorConf();
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));

    return useMemo(() => ({
        /**
         * Create new player. see [ims docs]{@link https://integration.playtech.com/apis/openapi/services/RegistrationService/operations/CreatePlayerInfo.html}
         * @memberOf Playtech.API.auth
         * @function
         * @param {Object} dataMap see [ims docs]{@link https://ims.ptdev.eu/apis/openapi/services/RegistrationService/types/CreatePlayerInfoDataMap.html}.
         * @param {Object} [addressDetails] see [ims docs]{@link https://ims.ptdev.eu/apis/openapi/services/RegistrationService/types/AddressDetails.html}.
         * @param {Object} [contactAddress] see [ims docs]{@link https://ims.ptdev.eu/apis/openapi/services/RegistrationService/types/ContactAddress.html}.
         * @param {Object} [depositLimits] see [ims docs]{@link https://ims.ptdev.eu/apis/openapi/services/ResponsibleGamingService/types/DepositLimit.html}.
         * @param {Object} [betLimits] see [ims docs]{@link https://ims.ptdev.eu/apis/openapi/services/ResponsibleGamingService/types/PlayerRgLimitChangeData.html}.
         * @param {string} [betLimitsAction] Values: 'logout', 'message'.
         * @param {Object} [lossLimits] see [ims docs]{@link https://ims.ptdev.eu/apis/openapi/services/ResponsibleGamingService/types/PlayerRgLimitChangeData.html}.
         * @param {string} [lossLimitsAction] Values: 'logout', 'message'.
         * @param {Object} [contactPreferences]
         * @param {string} [contactPreferences.channel] Values: 'email', 'phone', 'SMS', 'mobilePush', 'internalMessage', 'desktopCompanion', 'whatsApp', 'directMail'.
         * @param {string} [contactPreferences.type] Values: 'promotional', 'account', 'thirdParty'.
         * @param {boolean}[contactPreferences.preference]
         * @param {boolean} [contactPreferences.isInvalid] True if channel is marked as invalid.
         * @param {Object[]} [tags]
         * @param {string} [tags[].name]
         * @param {string} [tags[].value] Values: 'text', 'number', 'date', 'delta'
         * @param {string} [tags[].type]
         * @param {string} [tags[].comment]
         * @param {string} [tags[].expirationDate] Format: yyyy-MM-dd 2016-04-13.
         * @param {string} [token] Challenge-response token.
         * @param {string} [blackbox] Examples: SomestringForIOBB
         * @returns {Promise<{playerCode: number, userId: string, actions: Object}>} For actions type see [ims docs]{@link https://integration.playtech.com/apis/openapi/services/RegistrationService/types/ActionDataWrapper.html}.
         * @example
         * Playtech.API.auth.register({
         *       dataMap: {
         *           casinoName: 'playtech800075',
         *           clientSkin: 'playtech800075',
         *           title: 'Mr',
         *           firstname: 'test',
         *           lastname: 'test',
         *           sex: 'M',
         *           email: 'test2145@playtech.com',
         *           birthdate: '1986-01-01',
         *           countrycode: 'UA',
         *           state: 'Kyiv',
         *           city: 'Kyiv',
         *           zip: '01001',
         *           cellphone: '+38022112211',
         *           phone: '+380',
         *           address: 'svoboda street',
         *           userName: 'test2145',
         *           password: 'Password1!',
         *           verificationQuestion: 'test',
         *           verificationAnswer: 'test',
         *           currencyCode: 'GBP',
         *           coupon: '',
         *           advertiser: 'advertiser',
         *           clientPlatform: 'web',
         *           signupDeviceType: 'other',
         *           signupOsName: 'macintosh',
         *           signupOsVersion: 'x',
         *           signupDeliveryPlatform: 'Hub2',
         *           signupDeviceBrowser: 'chrome',
         *           language: 'EN',
         *           serial: '460916c7-bd41-42a8-ac82-d85a55924547',
         *           signupDeviceId: '460916c7-bd41-42a8-ac82-d85a55924547',
         *           clientType: 'casino'
         *       },
         *       contactPreferences: {
         *           channel: 'email',
         *           type: 'promotional',
         *           preference: false
         *       },
         *       addressDetails: {
         *           buildingName: 'My Building',
         *           buildingNumber: '1',
         *           subbuilding: '1',
         *           streetName: 'Sportivnaya',
         *           streetNumber: '1',
         *           propertyName: '-',
         *           propertyNumber: '12',
         *           suburb: 'Kyiv'
         *       },
         *       contactAddress: {
         *           city: 'Kyiv',
         *           countryCode: 'UA',
         *           zip: '01012'
         *       }
         * }).then(data => {...});
         */
        register,
        /**
         * Performs login by credentials.
         * @memberOf Playtech.API.auth
         * @function
         * @param {string} userName
         * @param {string} password
         * @param {boolean} [rememberMeLogin=false]
         * @returns {Promise<void>}
         * @example
         * Playtech.API.auth.login('test2145', 'Password1!').then(() => {...});
         */
        login: (
            userName: string,
            password: string,
            rememberMeLogin?: boolean,
            afterRegisterLogin?: boolean,
            loginMode?: TUserAuthLoginMode,
            fitToPlay?: boolean
        ) => loginByCredentialsWithRecaptchaPopup({
            afterRegisterLogin,
            fitToPlay,
            loginMode,
            password,
            rememberMeLogin,
            userName
        }),
        /**
         * Shows login popup.
         * @memberOf Playtech.API.auth
         * @function
         * @param {Function} [onClose] Callback on close popup
         * @param {Function} [onLogin] Callback on login
         * @param {string} [title] Title of popup
         * @example Playtech.API.auth.showLoginPopup({ onClose: (event) => {...}, onLogin: () => {...}, title: 'Login' });
         */
        showLoginPopup,
        /**
         * Show login page or login popup.
         * @memberOf Playtech.API.auth
         * @function
         * @example
         * Playtech.API.auth.showLogin();
         */
        showLogin,
        /**
         * @memberOf Playtech.API.auth
         * @function
         * @param {boolean} [isQuiet]
         * @returns {Promise<void>}
         * @example
         * Playtech.API.auth.logout().then(() => {...});
         */
        logout,
        /**
         * Get temporary authentication token for logged in user.
         * @memberOf Playtech.API.auth
         * @function
         * @param {string} [serviceType=GamePlay]
         * @returns {Promise<string>}
         * @example
         * Playtech.API.auth.getTemporaryAuthenticationToken();
         */
        getTemporaryAuthenticationToken,
        /**
         * @memberOf Playtech.API.auth
         * @returns {boolean}
         * @example
         * Playtech.API.auth.isLoggedIn;
         */
        isLoggedIn,
        /**
         * Add callback after login with credentials.
         * @memberOf Playtech.API.auth
         * @function
         * @param {Function} callback (resolve, reject) => {...}
         * @example
         * Playtech.API.auth.addAfterCredentialsLoginCallback((resolve, reject) => setTimeout(() => resolve(), 2000));
         */
         addAfterCredentialsLoginCallback,
        /**
         * Add callback to modify the data that is sent to IMS during registration.
         * It must return all arguments that it was given.
         * @memberOf Playtech.API.auth
         * @function
         * @param {Function} callback (dataMap) => {... return { dataMap }} dataMap from registration form
         * @example
         * Playtech.API.auth.addBeforeRegistrationHook(({ dataMap }) => {
         *     // modify phone right before registration request is sent
         *     dataMap.phone = '+123';
         *
         *     return { dataMap };
         * });
         */
        addBeforeRegistrationHook,
        /**
         * @private
         * This is only for Backbone registration.view.js module.
         */
        getBeforeRegistrationHandler,
         /**
         * Get whether fitToPlaySubmissionRequired is set to '1'.
         * @memberOf Playtech.API.auth
         * @function
         * @returns {boolean}
         * @example
         * Playtech.API.auth.getFitToPlaySubmissionRequired();
         */
        getFitToPlaySubmissionRequired,
        /** Get Two Factor configuration data. [Ims docs]{@link https://integration.playtech.com/apis/openapi/services/IMSConfigurationService/operations/GetTwoFactorConf.html}.
         * @memberOf Playtech.API.auth
         * @function
         * @returns
         * {Promise<{Object}>} {
         *     twoFactorConf: {
         *         enabled: boolean,
         *         loginDeviceSupported: boolean,
         *         rememberMeRequirePin: string,
         *         rememberMeWithoutTwoFactor: ('ON' | 'OFF'),
         *         rememberMeExpirationSec: number,
         *         loginDeviceExpirationSec: number,
         *         defaultChannel: string,
         *         onlyNumbers: boolean,
         *         unblockTokenExpirationSec: number,
         *         configuredServices: { service: string, option: ('ON' | 'OFF') }[],
         *         excludedClientTypePlatforms: { clientType: string, clientPlatform: string }[]
         *     }}
         * @example
         * Playtech.API.auth.getTwoFactorConfiguration().then(data => {...});
         */
        getTwoFactorConfiguration,
        /**
         * Aggregate multiple requests to IMS into one batch request. Could be used only after successful registration.
         * @memberOf Playtech.API.auth
         * @function
         * @param {Object[]} requests contains an array of requests to be sent to IMS.
         * @param {Object} authParameters contains auth parameters for the batch request.
         * @param {string} authParameters.playerName userId from registration response.
         * @param {string} [authParameters.email] user email.
         * @param {string} [authParameters.systemId] site systemId.
         * @param {boolean} [authParameters.realMode] demo or real mode.
         * @param {boolean} [authParameters.casinoName] site casinoName.
         * @example
         * Playtech.API.auth.sendBatchRequest({
         *      authParameters: { playerName: 'test123' },
         *      requests: [{
         *              ID: 35544,
         *              responses: [35545, 35546],
         *              description: 'Set player tags',
         *              tags,
         *              errorLevel: 1
         *      }]
         * });
         */
        sendBatchRequest: batchRequest,
        /** Validates current session. Allows to check if MFA 2FA, etc is required. [Ims docs]{@link https://integration.playtech.com/apis/openapi/services/AuthenticationService/operations/ValidateLoginSession.html}.
         * @memberOf Playtech.API.auth
         * @function
         * @returns
         * {Promise<{Object}>} {
         *    sessionValidationByMfa?: {
         *      provider: string,
         *      properties: { name: string, value: string }[]
         *   }
         * }
         * @example
         * Playtech.API.auth.validateSession().then(data => {...});
         */
        validateSession: validateLoginSession,
        /** Resend 2FA token to user upon login flow.
         * @memberOf Playtech.API.auth
         * @function
         * @returns {Promise<void>}
         * @example
         * Playtech.API.auth.resendLogin2FAToken();
         */
        resendLogin2FAToken,
        /** Runs the action of the token generated by PrepareAction call. [Ims docs]{@link https://integration.playtech.com/apis/openapi/services/UserService/operations/InvokeAction.html}.
         * @memberOf Playtech.API.auth
         * @function
         * @param {Object} options invokeAction options.
         * @param {string} options.token Token that was generated in PrepareAction call.
         * @param {string} [options.challengeResponse] Response to challenge. To be used only if challenge was also returned in PrepareAction call response.
         * @param {Object[]} [options.parameters] Action specific parameters as key-value list like so [{ key: 'key1', value: 'value1' }].
         * @returns
         * {Promise<{Object}>} {
         *    result: [{
         *      key: string,
         *      value: string
         *   }]
         * }
         * @example
         * Playtech.API.auth.invokeAction({ token: 'tokenFromIMS' }).then(data => {...});
         */
        invokeAction,
        /** Login by temporary token.
         * @memberOf Playtech.API.auth
         * @function
         * @param {string} userName User name to login with.
         * @param {string} token Temporary token to login with.
         * @param {string} [loginMode] used to determine the type of token provided (e.g externalToken).
         * @returns {Promise<void>}
         * @example
         * Playtech.API.auth.loginByTemporaryToken({ userName: 'testUser', token: 's-m1gTrP1qQeitJjwvHj8NDCBekaSAgHuZlGSUFqOQQMk-A0Xvif0xVLEBveYHinVbpCyZrfpDVXItCeT6-HE1rw' });
         */
        loginByTemporaryToken
    }), [
        addAfterCredentialsLoginCallback,
        addBeforeRegistrationHook,
        batchRequest,
        getBeforeRegistrationHandler,
        getTemporaryAuthenticationToken,
        getTwoFactorConfiguration,
        invokeAction,
        isLoggedIn,
        loginByCredentialsWithRecaptchaPopup,
        loginByTemporaryToken,
        logout,
        register,
        showLogin,
        showLoginPopup,
        validateLoginSession
    ]);
};
